import { defineStore, acceptHMRUpdate } from 'pinia';
import ProjectSrv from '@/components/Projects/ProjectSrv';
import Planning from '@/models/Planning';
import Action from '@/models/Action';

function projectUrl(projectId, projectTitle, isConsult) {
  return `/${isConsult ? 'viewer/' : 'planning/'}${projectId}/${window.slugify(projectTitle || "New project")}`;
}

const store = defineStore('HomeUser', {
  state: () => ({
    loaded: false,
    plannings: [],
    actions: [],
    elements: [],
    keyDates: null, // WPD specific
    actionsByStatus: { late: [], urgent: [], current: [] },
    notifications: [],
    comments: [],
  }),
  getters: {
    planningElementsById() { // id : pXXeYY
      /*
        this acts as the elements database shared by reference -> auto synchronize actions with elements in kanban
        this is required within actions in case 2 actions come from the same task : without, each action.element would overwrite the other on saving
      */
      const elementsMap = new Map();
      this.plannings.forEach((planning) => {
        planning.elements.forEach((element) => {
          elementsMap.set(`p${planning.id}e${element.id}`, element);
        });
      });

      return elementsMap;
    },
    planningsDataCount() {
      const planningsDataCount = {};
      this.plannings.forEach((planning) => {
        const actionsLateCount = this.actionsByStatus.late.filter(action => action.getPlanningId() == planning.id && ! action.getChecked()).length;
        const actionsUrgentCount = this.actionsByStatus.urgent.filter(action => action.getPlanningId() == planning.id && ! action.getChecked()).length;
        const notificationsCount = this.notifications.filter(notification => notification.section_type == 'planning' && notification.section_id == planning.id).length;
        const elementsCount = this.elements.filter(el => el.getPlanning().id == planning.id).length;
        const commentsCount = this.comments.filter(comment => comment.planning_id == planning.id).length;
        const priority = 1000 * notificationsCount + 100 * (actionsLateCount + commentsCount) + 10 * actionsUrgentCount + elementsCount;
        if (priority) planningsDataCount[planning.id] = { actionsLateCount, actionsUrgentCount, notificationsCount, elementsCount, commentsCount, priority };
      });
      return planningsDataCount;
    },
    planningsByPriority() {
      return this.plannings.filter(planning => this.planningsDataCount[planning.id]).sort((a, b) => this.planningsDataCount[b.id].priority - this.planningsDataCount[a.id].priority);
    },
  },
  actions: {
    load() {
      return window.apiSrv.call('dashboards/home2', 'index').then((result) => {
        const { actions, elements, comments, notifications, filtered_plannings, meeting_actions, key_dates } = result.data;

        const elementsMap = new Map();

        this.plannings = filtered_plannings.map((project) => {
          const content = typeof project.content == 'object' ? project.content : JSON.parse(project.content);
          content.id = project.id;
          content.meta = ProjectSrv.getContentMeta(project);
          content.meta.url = projectUrl(project.id, project.title, project.access_right == 'consult');
          content.elements.forEach((element) => {
            element.config = {
              'show-description': false,
              'show-dates': false,
              'show-date': false,
              'show-progress': true,
              "show-step": true,
              'show-checklist': true,
              'show-users': false,
              'show-links': false,
              'show-budgets': false,
              'show-projecttitle': false,
            };
          });
          const planning = new Planning(content);
          planning.elements.forEach((element) => {
            const laneLabel = planning.lanes.find(item => item.id == element.getLaneId())?.label;
            element.display_title = laneLabel ? `↪ ${laneLabel}` : '';
            elementsMap.set(`p${planning.id}e${element.id}`, element);
          });
          return planning;
        });

        const actionsByStatus = { late: [], urgent: [], current: [] };
        this.actions = actions.map((action) => {
          const element = elementsMap.get(`p${action.project_id}e${action.element_id}`);
          const item = action.id ? element.getChecklist().find(checklistItem => checklistItem.id == action.id) : element.getChecklist()[action.index];
          const newAction = new Action({ type: 'planning', element, item });
          actionsByStatus[action.status].push(newAction);
          return newAction;
        });
        meeting_actions.forEach((action) => {
          const newAction = new Action(action);
          this.actions.push(newAction);
          actionsByStatus[action.status].push(newAction);
        });
        ['late', 'urgent', 'current'].forEach((status) => {
          actionsByStatus[status].sort((a, b) => {
            if (a.getEndTime() != b.getEndTime()) return a.getEndTime() < b.getEndTime() ? -1 : 1;
            if (a.type != b.type) return a.type < b.type ? -1 : 1;
            return a.getPlanningId() < b.getPlanningId() ? -1 : 1;
          });
        });
        this.actionsByStatus = actionsByStatus; // performance gap with direct push

        this.elements = elements.map(({ id, project_id }) => elementsMap.get(`p${project_id}e${id}`));

        if (key_dates) { // WPD specific
          this.keyDates = key_dates.map(({ id, project_id }) => elementsMap.get(`p${project_id}e${id}`)).sort((a, b) => {
            const aStartTime = a.getStartTime();
            const bStartTime = b.getStartTime();
            if (aStartTime.isSame(bStartTime, 'day')) return a.project_id < b.project_id ? -1 : 1;
            return aStartTime.isBefore(bStartTime) ? -1 : 1;
          });
        }

        this.notifications = notifications;
        this.comments = comments;
        this.loaded = true;
      });
    },
  },
});

if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(store, import.meta.hot));
export default store;
